<template>
    <div class="setup-container">
        <div class="page-hero">
            <div>
                <p class="eyebrow">Permissions</p>
                <h1>Assign Route</h1>
                <p class="hero-copy">Connect routes to roles so each role gets the correct navigation access.</p>
            </div>
            <button class="setup-btn" @click="loadData">
                <RefreshCcw class="icon" />
                Refresh
            </button>
        </div>

        <section class="panel">
            <form class="setup-form" @submit.prevent="submitForm">
                <div class="form-group">
                    <label for="roleId">Role</label>
                    <select id="roleId" v-model="form.roleId" required>
                        <option value="" disabled>Select role</option>
                        <option v-for="role in roles" :key="role.RoleId" :value="role.RoleId">
                            {{ role.RoleName }}
                        </option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="routeId">Route</label>
                    <select id="routeId" v-model="form.routeId" required>
                        <option value="" disabled>Select route</option>
                        <option v-for="route in routes" :key="route.RouteId" :value="route.RouteId">
                            {{ route.RouteName }} - {{ route.RoutePath }}
                        </option>
                    </select>
                </div>

                <div class="buttons mt-3">
                    <button type="submit">Assign Route</button> &nbsp;
                    <button type="button" @click="resetForm">Reset</button>
                </div>
            </form>
        </section>

        <section class="panel">
            <div class="header-search">
                <div>
                    <p class="section-kicker">Assignments</p>
                    <h2>Role routes</h2>
                </div>
                <div class="search-bar">
                    <Search />
                    <input v-model="searchQuery" type="text" placeholder="Search assignments..." />
                </div>
            </div>

            <table class="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Role</th>
                        <th>Route</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="paginatedAssignments.length === 0">
                        <td colspan="3" class="no-data">No route assignments found.</td>
                    </tr>
                    <tr v-else v-for="(item, index) in paginatedAssignments" :key="`${item.RoleId}-${item.RouteId}`">
                        <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                        <td>{{ item.RoleName || `Role #${item.RoleId}` }}</td>
                        <td>{{ item.RouteName || `Route #${item.RouteId}` }}</td>
                    </tr>
                </tbody>
            </table>

            <div class="pagination">
                <button @click="prevPage" :disabled="currentPage === 1">
                    <ChevronLeft />
                </button>
                <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="{ active: currentPage === page }"
                >
                    {{ page }}
                </button>
                <button @click="nextPage" :disabled="currentPage === totalPages">
                    <ChevronRight />
                </button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { ChevronLeft, ChevronRight, RefreshCcw, Search } from "lucide-vue-next";
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";

const rolesApi = "/roles_lists.php";
const routesApi = "/routes_lists.php";
const assignmentsApi = "/role_routes_lists.php";
const createApi = "/role_routes_setup.php";

const roles = ref([]);
const routes = ref([]);
const assignments = ref([]);
const searchQuery = ref("");
const currentPage = ref(1);
const pageSize = 8;

const form = ref({
    roleId: "",
    routeId: ""
});

const filteredAssignments = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return assignments.value;

    return assignments.value.filter((item) => {
        return (
            String(item.RoleName || "").toLowerCase().includes(query) ||
            String(item.RouteName || "").toLowerCase().includes(query) ||
            String(item.RoutePath || "").toLowerCase().includes(query)
        );
    });
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredAssignments.value.length / pageSize)));

const paginatedAssignments = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return filteredAssignments.value.slice(start, start + pageSize);
});

watch(searchQuery, () => {
    currentPage.value = 1;
});

function goToPage(page) {
    if (page >= 1 && page <= totalPages.value) currentPage.value = page;
}

function prevPage() {
    if (currentPage.value > 1) currentPage.value--;
}

function nextPage() {
    if (currentPage.value < totalPages.value) currentPage.value++;
}

function resetForm() {
    form.value.roleId = "";
    form.value.routeId = "";
}

async function loadData() {
    try {
        const [rolesResponse, routesResponse, assignmentsResponse] = await Promise.all([
            axios.get(rolesApi),
            axios.get(routesApi),
            axios.get(assignmentsApi)
        ]);

        roles.value = rolesResponse?.data?.data ?? [];
        routes.value = routesResponse?.data?.data ?? [];
        assignments.value = assignmentsResponse?.data?.data ?? [];
    } catch (error) {
        console.error(error);
    }
}

async function submitForm() {
    try {
        const payload = new FormData();
        payload.append("RoleId", form.value.roleId);
        payload.append("RouteId", form.value.routeId);

        const response = await axios.post(createApi, payload, {
            headers: { "Content-Type": "multipart/form-data" }
        });

        alert(response?.data?.message || "Route assigned successfully.");
        resetForm();
        await loadData();
    } catch (error) {
        alert(error?.response?.data?.message || error.message);
    }
}

onMounted(loadData);
</script>

